import { IUserProfile } from "./interfaces";
import { RoleType } from "../constants/enums";
import Helpers from "./helpers";

export type IRoleBitMask = {
    roleSet: RoleType.Service | RoleType.TenantOrMembership | RoleType.Normal | RoleType.User;
    roleDetailType: RoleType.Owner | RoleType.Admin | RoleType.GroupAdmin | RoleType.Member | RoleType.Custom | RoleType.Default;
};

const checkRoleDetailType = (
    role: number,
    roleDetailType: RoleType.Owner | RoleType.Admin | RoleType.GroupAdmin | RoleType.Member | RoleType.Custom | RoleType.Default
) => (role & roleDetailType) === roleDetailType;

const checkRoleSet = (role: number, roleSet: RoleType.Service | RoleType.TenantOrMembership | RoleType.Normal | RoleType.User) =>
    (role & roleSet) === roleSet;

export const IDENTITY_SERVICE_CODE = "000001";
// Note: - OrganizationType.Main => Check trên bộ Tenant || Ngược lại check trên bộ Organization
//       - Role set: Service > Tenant > Normal

export const RoleHelpers = {
    getRole: (roleType: number): IRoleBitMask => {
        let result: IRoleBitMask = { roleSet: RoleType.User, roleDetailType: RoleType.Default };
        const bit16 = [RoleType.Service, RoleType.TenantOrMembership, RoleType.Normal, RoleType.User] as const;
        const bit8 = [RoleType.Owner, RoleType.Admin, RoleType.GroupAdmin, RoleType.Member, RoleType.Custom, RoleType.Default] as const;
        for (const fBit of bit16) {
            if ((roleType & fBit) != fBit) continue;
            if ((roleType & fBit) == fBit) {
                for (const sBit of bit8) {
                    if ((roleType & ~fBit) == sBit) {
                        result.roleSet = fBit;
                        result.roleDetailType = sBit;
                        return result;
                    }
                }
            }
        }
        return result;
    },

    // Check Role Detail
    isOwner: (roleType: number) => checkRoleDetailType(roleType, RoleType.Owner),
    isAdmin: (roleType: number) => checkRoleDetailType(roleType, RoleType.Admin),
    isGroupAdmin: (roleType: number) => checkRoleDetailType(roleType, RoleType.GroupAdmin),
    isMember: (roleType: number) => checkRoleDetailType(roleType, RoleType.Member),
    isCustom: (roleType: number) => checkRoleDetailType(roleType, RoleType.Custom),
    isDefault: (roleType: number) => checkRoleDetailType(roleType, RoleType.Default),

    // Check Role Set
    isServiceSet: (roleType: number) => checkRoleSet(roleType, RoleType.Service),
    isTenantSet: (roleType: number) => checkRoleSet(roleType, RoleType.TenantOrMembership),
    isOrganizationSet: (roleType: number) => checkRoleSet(roleType, RoleType.Normal),
    isUserSet: (roleType: number) => checkRoleSet(roleType, RoleType.User),

    // Bộ service
    isServiceAdmin: (roleType: number) => RoleHelpers.isServiceSet(roleType) && RoleHelpers.isAdmin(roleType),
    isServiceGroupAdmin: (roleType: number) => RoleHelpers.isServiceSet(roleType) && RoleHelpers.isGroupAdmin(roleType),
    isServiceMember: (roleType: number) => RoleHelpers.isServiceSet(roleType) && RoleHelpers.isMember(roleType),
    isServiceCustom: (roleType: number) => RoleHelpers.isServiceSet(roleType) && RoleHelpers.isCustom(roleType),
    isServiceDefault: (roleType: number) => RoleHelpers.isServiceSet(roleType) && RoleHelpers.isDefault(roleType),

    // Bộ tenant
    isTenantOwner: (roleType: number) => RoleHelpers.isTenantSet(roleType) && RoleHelpers.isOwner(roleType),
    isTenantAdmin: (roleType: number) => RoleHelpers.isTenantSet(roleType) && RoleHelpers.isAdmin(roleType),
    isTenantGroupAdmin: (roleType: number) => RoleHelpers.isTenantSet(roleType) && RoleHelpers.isGroupAdmin(roleType),
    isTenantMember: (roleType: number) => RoleHelpers.isTenantSet(roleType) && RoleHelpers.isMember(roleType),
    isTenantCustom: (roleType: number) => RoleHelpers.isTenantSet(roleType) && RoleHelpers.isCustom(roleType),
    isTenantDefault: (roleType: number) => RoleHelpers.isTenantSet(roleType) && RoleHelpers.isDefault(roleType),

    // Bộ tổ chức
    isOrganizationOwner: (roleType: number) => RoleHelpers.isOrganizationSet(roleType) && RoleHelpers.isOwner(roleType),
    isOrganizationAdmin: (roleType: number) => RoleHelpers.isOrganizationSet(roleType) && RoleHelpers.isAdmin(roleType),
    isOrganizationGroupAdmin: (roleType: number) => RoleHelpers.isOrganizationSet(roleType) && RoleHelpers.isGroupAdmin(roleType),
    isOrganizationMember: (roleType: number) => RoleHelpers.isOrganizationSet(roleType) && RoleHelpers.isMember(roleType),
    isOrganizationCustom: (roleType: number) => RoleHelpers.isOrganizationSet(roleType) && RoleHelpers.isCustom(roleType),
    isOrganizationDefault: (roleType: number) => RoleHelpers.isOrganizationSet(roleType) && RoleHelpers.isDefault(roleType),

    // Bộ Người dùng
    isUserOwner: (roleType: number) => RoleHelpers.isUserSet(roleType) && RoleHelpers.isOwner(roleType),
    isUserAdmin: (roleType: number) => RoleHelpers.isUserSet(roleType) && RoleHelpers.isAdmin(roleType),
    isUserGroupAdmin: (roleType: number) => RoleHelpers.isUserSet(roleType) && RoleHelpers.isGroupAdmin(roleType),
    isUserMember: (roleType: number) => RoleHelpers.isUserSet(roleType) && RoleHelpers.isMember(roleType),
    isUserCustom: (roleType: number) => RoleHelpers.isUserSet(roleType) && RoleHelpers.isCustom(roleType),
    isUserDefault: (roleType: number) => RoleHelpers.isUserSet(roleType) && RoleHelpers.isDefault(roleType),

    // Another
    isSuperAdmin: (roleType: number, serviceCode: string) => RoleHelpers.isServiceAdmin(roleType) && serviceCode === IDENTITY_SERVICE_CODE,
    isOrganizationUser: (userProfile: IUserProfile) => {
        if (userProfile?.organizationId === "0") return false;
        if (Number(userProfile?.organizationId) === 0) return false;
        if (!Helpers.isNullOrEmpty(userProfile?.organizationId)) return true;
        return false;
    },
};
